import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Mail, RefreshCw } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { authApi } from "../../services/api";

export default function Signup() {
  const { signup } = useAuth();
  const logoUrl = `${process.env.PUBLIC_URL}/logo.png`;
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", agreed: false });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(false);
  const [deliveryFailed, setDeliveryFailed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  function handleChange(event) {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    setError("");
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!form.name || !form.email || !form.password) {
      setError("Name, email, and password are required.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!form.agreed) {
      setError("You must agree to the Terms & Conditions.");
      return;
    }

    setLoading(true);
    try {
      const result = await signup({
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone || undefined,
      });
      setDeliveryFailed(result.emailDelivery === "failed");
      setCreated(true);
    } catch (err) {
      setError(err.response?.data?.message || "Could not create account. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResending(true);
    setError("");
    try {
      await authApi.resendVerification(form.email);
      setResent(true);
      setDeliveryFailed(false);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to resend the verification email.");
    } finally {
      setResending(false);
    }
  }

  const steps = ["Create account", "Join a pool", "Win together"];

  return (
    <div className="auth-shell">
      <div className="auth-split">
        <div className="auth-panel-left">
          <div className="auth-panel-brand">
            <span className="auth-logo">
              <img src={logoUrl} alt="Lucky Pool logo" className="auth-logo-image" />
            </span>
          </div>
          <div className="auth-panel-body">
            <h2>Three steps to your first draw.</h2>
            <p>Getting started takes less than two minutes. No experience needed.</p>
            <div className="auth-steps">
              {steps.map((step, index) => (
                <div className="auth-step" key={step}>
                  <span className="auth-step-num">{index + 1}</span>
                  <span>{step}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="auth-panel-footer"><span>app.luckypool.com</span></div>
        </div>

        <div className="auth-panel-right">
          <div className="auth-card">
            {created ? (
              <div className="auth-sent-state">
                <span className="auth-sent-icon"><Mail size={32} /></span>
                <h1>Verify your email</h1>
                <p className="auth-sub">
                  {deliveryFailed
                    ? "Your account was created, but the verification email could not be delivered. Try sending it again."
                    : <>We sent a verification link to <strong>{form.email}</strong>.</>}
                </p>
                {resent && <div className="auth-success">A new verification link has been requested.</div>}
                {error && <div className="auth-error" role="alert">{error}</div>}
                <button type="button" className="auth-submit" onClick={handleResend} disabled={resending}>
                  {resending ? <span className="auth-spinner" /> : <><RefreshCw size={16} /> Resend email</>}
                </button>
                <p className="auth-switch"><Link to="/login">Back to sign in</Link></p>
              </div>
            ) : (
              <>
                <div className="auth-card-header">
                  <h1>Create account</h1>
                  <p className="auth-sub">Join the pool. It only takes a minute.</p>
                </div>
                {error && <div className="auth-error" role="alert">{error}</div>}
                <form onSubmit={handleSubmit} noValidate>
                  <div className="auth-field">
                    <label htmlFor="name">Full Name</label>
                    <input id="name" name="name" type="text" required value={form.name} onChange={handleChange} placeholder="Jane Smith" />
                  </div>
                  <div className="auth-field">
                    <label htmlFor="email">Email address</label>
                    <input id="email" name="email" type="email" autoComplete="email" required value={form.email} onChange={handleChange} placeholder="you@example.com" />
                  </div>
                  <div className="auth-field">
                    <label htmlFor="phone">Phone <span className="auth-optional">(optional)</span></label>
                    <input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="+1 555 000 0000" />
                  </div>
                  <div className="auth-field">
                    <label htmlFor="sig-password">Password</label>
                    <div className="auth-input-wrap">
                      <input id="sig-password" name="password" type={showPass ? "text" : "password"} autoComplete="new-password" required value={form.password} onChange={handleChange} placeholder="Min. 8 characters" />
                      <button type="button" className="auth-eye" onClick={() => setShowPass((shown) => !shown)} aria-label="Toggle password visibility">
                        {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                      </button>
                    </div>
                  </div>
                  <label className="auth-checkbox">
                    <input type="checkbox" name="agreed" checked={form.agreed} onChange={handleChange} />
                    <span>I agree to the <a href="https://luckypool.com/terms" target="_blank" rel="noreferrer">Terms &amp; Conditions</a> and <a href="https://luckypool.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a></span>
                  </label>
                  <button type="submit" className="auth-submit" disabled={loading}>
                    {loading ? <span className="auth-spinner" /> : "Create Account"}
                  </button>
                </form>
                <p className="auth-switch">Already have an account? <Link to="/login">Sign in</Link></p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
